import React from 'react';
import { customFieldAPI } from '../../services/api';
import './customFieldInputs.css';

// Admin-defined custom fields on an intake form — the Add/Edit Project modal and the
// Add/Edit Contact modal.
//
// Shared rather than written twice: the two modals are 600 lines each, and a copy of
// this in both is how "dropdowns work on jobs but not contacts" happens six months
// from now.
//
// Only fields whose visibility is Intake appear. Detail fields belong on the record's
// own page, and Hidden ones exist for an integration — never for a person to type.

// Loads the definitions and values, tracks edits, and saves. Returns everything the
// form needs, so a modal wires this in three lines.
//
// entity: 'Project' | 'Contact'
// recordId: the record being edited, or null/undefined when creating
export function useIntakeCustomFields(entity, isOpen, recordId) {
    const [fields, setFields] = React.useState([]);
    const [values, setValues] = React.useState({});
    // Kept so a save sends only what changed. Without it, saving a job would rewrite
    // every field including the Detail and Hidden ones this form never displayed.
    const [initial, setInitial] = React.useState({});

    React.useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        customFieldAPI.getDefinitions(entity)
            .then((res) => {
                if (cancelled) return;
                setFields((res.data || []).filter(
                    (d) => d.isActive && d.visibility === 'Intake'
                ));
            })
            // Additive: a custom field that will not load must not stop someone
            // entering a job or a contact.
            .catch(() => setFields([]));
        return () => { cancelled = true; };
    }, [entity, isOpen]);

    React.useEffect(() => {
        if (!isOpen || !recordId) {
            setValues({});
            setInitial({});
            return;
        }
        let cancelled = false;
        customFieldAPI.getValues(entity, recordId)
            .then((res) => {
                if (cancelled) return;
                const map = {};
                (res.data || []).forEach((r) => {
                    if (r.value != null) map[r.definition.id] = String(r.value);
                });
                setValues(map);
                setInitial(map);
            })
            .catch(() => { setValues({}); setInitial({}); });
        return () => { cancelled = true; };
    }, [entity, isOpen, recordId]);

    const setValue = React.useCallback((definitionId, value) => {
        setValues((prev) => ({ ...prev, [definitionId]: value }));
    }, []);

    // Called with the record's id once it exists — a new record has to be created
    // before values can hang off it, same as section crewing on a project.
    const save = React.useCallback(async (id) => {
        if (!id || fields.length === 0) return;

        const changed = fields
            .map((d) => ({ definitionId: d.id, value: values[d.id] ?? '' }))
            .filter((item) => item.value !== (initial[item.definitionId] ?? ''));

        if (changed.length === 0) return;

        // Additive, like extra contacts: a record that saved but whose custom field
        // did not must not report the whole save as failed.
        await customFieldAPI.saveValues(entity, id, changed).catch(() => {});
    }, [entity, fields, values, initial]);

    return { fields, values, setValue, save };
}


// Admin-set choices for the model's own fields, keyed by property name in the casing
// the model uses.
//
// Separate from custom fields because these are real columns: a built-in field has no
// definition row, and its type is fixed by the schema. All this can change is the
// control the form offers for it — a text column with choices becomes a dropdown.
export function useBuiltInFieldConfig(entity, isOpen) {
    const [config, setConfig] = React.useState({});

    React.useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        customFieldAPI.getBuiltIn(entity)
            .then((res) => {
                if (cancelled) return;
                const map = {};
                (res.data || []).forEach((f) => {
                    if (f.configuredOptions?.length || f.label) {
                        map[f.name] = { options: f.configuredOptions || [], label: f.label };
                    }
                });
                setConfig(map);
            })
            // The form falls back to the controls it has always had, which is the
            // right failure: a missing dropdown is better than a missing field.
            .catch(() => setConfig({}));
        return () => { cancelled = true; };
    }, [entity, isOpen]);

    return config;
}
// One field's control, chosen by the definition's type.
function Control({ def, value, onChange }) {
    const id = `cf-${def.id}`;

    if (def.type === 'Select') {
        return (
            <select id={id} className="form-input" value={value} onChange={(e) => onChange(e.target.value)}>
                <option value="">— none —</option>
                {(def.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        );
    }

    if (def.type === 'Checkbox') {
        return (
            <label className="cfi-checkbox">
                <input
                    id={id}
                    type="checkbox"
                    className="form-checkbox"
                    checked={value === 'true'}
                    // Stored as the same text the record's own page writes, so a value
                    // set here and one set there compare equal in an automation
                    // condition.
                    onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
                />
                Yes
            </label>
        );
    }

    if (def.type === 'TextArea') {
        return (
            <textarea
                id={id}
                className="form-textarea"
                rows="2"
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        );
    }

    return (
        <input
            id={id}
            type={def.type === 'Number' ? 'number' : def.type === 'Date' ? 'date' : 'text'}
            className="form-input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
        />
    );
}

// Renders NOTHING when no intake fields are defined, which is every install's
// starting state. An empty "Additional Details" heading on every job and every
// contact would be worse than no feature at all.
function CustomFieldInputs({ fields, values, setValue, title = 'Additional Details' }) {
    if (!fields || fields.length === 0) return null;

    // Grouped by the section set on the admin page, so related fields sit together
    // instead of in one flat list.
    const groups = fields.reduce((acc, def) => {
        const key = def.section || title;
        (acc[key] = acc[key] || []).push(def);
        return acc;
    }, {});

    return (
        <div className="form-section">
            <h3 className="section-title">{title}</h3>
            {Object.entries(groups).map(([groupName, defs]) => (
                <div key={groupName}>
                    {/* Only worth a sub-heading once the fields are actually split
                        across more than one section. */}
                    {groupName !== title && <h4 className="cfi-group">{groupName}</h4>}
                    <div className="form-row">
                        {defs.map((def) => (
                            <div className="form-group" key={def.id}>
                                <label className="form-label" htmlFor={`cf-${def.id}`}>{def.label}</label>
                                <Control
                                    def={def}
                                    value={values[def.id] || ''}
                                    onChange={(v) => setValue(def.id, v)}
                                />
                                {def.helpText && <small className="form-hint">{def.helpText}</small>}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default CustomFieldInputs;
